import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { buildWorkspacePath, command, findActionForModel, groupByEntryField, popFrame, resolveSpecial, substituteActiveId, substituteActiveRecord } from "../core";
import type { Domain, FormButtonElement, Json, ListQuery, ListRowButton, RecordData, SearchFilter } from "../core";
import { useAssistantBindingSetter, buildViewState } from "../react/providers/AssistantBindingProvider";
import type { ViewIntentOp } from "./assistant/types";
import { useWorkspaceNavigation } from "../react/navigation/useWorkspaceNavigation";
import { useCommand } from "../react/hooks/useCommand";
import { useBootstrap } from "../react/queries/useBoot";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { useSession } from "../react/providers/SessionProvider";
import { useNavigate } from "@tanstack/react-router";
import { useActionFilters, useActionLoad, useActionRecords, useGroupOptions, useRecord } from "../react/queries/useAction";
import { useReportActions } from "../react/queries/useReports";
import { deliverReport } from "../core";
import type { PrintItem, ActionItem } from "./FormControlPanel";
import { ActionControlPanel } from "./ActionControlPanel";
import type { Crumb } from "./ActionControlPanel";
import { ListToolbarSlots } from "./listToolbarSlots";
import { FormControlPanel } from "./FormControlPanel";
import { useDialogActionStack } from "./DialogActionStack";
import { ViewLoader } from "./ViewLoader";
import { confirm as confirmDialog } from "./ConfirmDialog";
import { SearchPanel } from "./SearchPanel";
import { SelectionActions } from "./SelectionActions";
import { ClientActionPage } from "./ClientActionPage";
import { ListView } from "./ListView";
import { FormView } from "./FormView";
import { RecordProvider } from "../react/form";
import { KanbanView } from "./KanbanView";
import type { GroupOption } from "./KanbanView";

const REFERENCE_COLUMN_LIMIT = 10;

const PAGE_SIZE = 80;

// DSL `context="..."` is authored as a JSON-encoded literal (e.g. `{"force":true}`).
// Backend handlers want a real object, not the raw string. Returns null when the
// attribute isn't a parseable JSON value — keeps the dispatch path safe.
function safeParseJson(value: string): Json | null {
    try {
        const parsed: unknown = JSON.parse(value);
        return parsed as Json;
    } catch {
        return null;
    }
}

// Enh 10 — turn a record-bound action's `context` template into seed values for
// the opened wizard's draft. Keys are `default_<field>`; the special value
// "active_id" is replaced with the open record's uuid.
//   {"default_user_id": "active_id"}  →  {user_id: <activeId>}
function buildBoundActionDefaults(
    contextJson: string,
    activeId: string,
    activeDisplayName?: string,
): Record<string, Json> {
    const out: Record<string, Json> = {};
    const parsed = contextJson ? safeParseJson(contextJson) : null;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return out;
    for (const [k, v] of Object.entries(parsed as Record<string, Json>)) {
        if (!k.startsWith("default_")) continue;
        // "active_id" → the open record. Seed the enriched M2O shape
        // {id, display_name} (matching how loaded records carry references) so
        // the picker renders the record's name, not the raw uuid. A literal
        // value passes through unchanged.
        out[k.slice("default_".length)] = v === "active_id"
            ? { id: activeId, display_name: activeDisplayName ?? activeId }
            : v;
    }
    return out;
}

// Build the `onSaved` handler for a record-bound action that opts into
// "log out when the user changes their OWN record" via a `logout_on_self`
// directive in its `context` — the field name to compare against the current
// user (e.g. "user_id"). Returns undefined when the directive is absent.
// `doLogout` fires when the saved record's <field> equals currentUserId.
function buildSelfLogoutHandler(
    contextJson: string,
    currentUserId: string,
    doLogout: () => void,
): ((saved: Record<string, Json>) => void) | undefined {
    const parsed = contextJson ? safeParseJson(contextJson) : null;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return undefined;
    const field = (parsed as Record<string, Json>)["logout_on_self"];
    if (typeof field !== "string" || !field) return undefined;
    return (saved: Record<string, Json>) => {
        const raw = saved?.[field];
        const id = typeof raw === "string"
            ? raw
            : raw && typeof raw === "object" && !Array.isArray(raw)
                ? String((raw as Record<string, unknown>).id ?? (raw as Record<string, unknown>).record_uuid ?? "")
                : "";
        if (id && id === currentUserId) doLogout();
    };
}

export function ActionManager() {
    const dispatch = useCommand();
    const runtime = useRuntime();
    const session = useSession();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // Enh 03 S4 — strip chip → timeline jump signal + chatter mutation epoch
    // (timeline refetch). Owned here because the strip lives in the control
    // panel while the timeline renders inside FormView.
    const [timelineJump, setTimelineJump] = useState<{ filter: import("./RecordStrip").TimelineFilter; n: number } | null>(null);
    const [chatterEpoch, setChatterEpoch] = useState(0);
    const { stack, orgCode, navigateTo } = useWorkspaceNavigation();
    const dialogStack = useDialogActionStack();
    const boot = useBootstrap();
    const action = stack[0];

    // Drill-stack support (WS 4.7): when the URL carries record frames after
    // the root action, the LAST frame is what we render. Two kinds of drill
    // frame exist:
    //   - RecordFrame (m2o "open")  → render the form for a related record;
    //     resolve action via `findActionForModel(boot, modelKey)`.
    //   - ListDrillFrame (Related ▾ chip click, Enhancement 06) → render the
    //     list/kanban/calendar view of a bespoke action; the URL already
    //     names the action slug, and the action carries the domain template
    //     that we substitute `active_id` into at render-time.
    const drillFrames = (stack.length > 1 ? stack.slice(1) : []) as import("../core").DrillFrame[];
    const drillFrame = drillFrames.length > 0 ? drillFrames[drillFrames.length - 1] : null;

    const recordDrill = drillFrame && drillFrame.kind === "record" ? drillFrame : null;
    const listDrill = drillFrame && drillFrame.kind === "list" ? drillFrame : null;

    const recordDrillSlug = recordDrill ? findActionForModel(boot.data, recordDrill.modelKey) : null;
    // Orphan only applies to record drills (list drills carry their own
    // actionSlug in the URL — never orphaned).
    const isOrphanDrill = Boolean(recordDrill) && !recordDrillSlug;

    // Shadow slug / viewMode / recordRef so the existing record/list/kanban
    // branches naturally target the drilled context.
    const slug = listDrill
        ? listDrill.actionSlug
        : recordDrillSlug
            ? recordDrillSlug
            : action.actionKey;
    // When a list drill carries a recordRef (a row was opened inside the
    // drilled list), switch to form mode so the form renders for that
    // record while the drilled-list breadcrumb context survives.
    // `action.viewMode` is undefined for client actions (no view) — coalesce to
    // "list" here so every window-render branch keeps a concrete view mode.
    // Client actions never reach those branches: they mount via ClientActionPage
    // (the clientComponent check below) before any view-mode logic runs.
    const viewMode = listDrill
        ? (listDrill.recordRef ? "form" : listDrill.viewMode)
        : recordDrill && recordDrillSlug
            ? "form"
            : (action.viewMode ?? "list");
    const recordRef = listDrill
        ? listDrill.recordRef
        : recordDrill && recordDrillSlug
            ? recordDrill.recordRef
            : action.recordRef;

    // For list-drill, compute the active_id from the closest upstream
    // record context. That can be EITHER an explicit RecordFrame OR a
    // list-drill frame that itself carries a recordRef (a row opened inside
    // a drilled list). Fall back to the root action's recordRef.
    const activeId = listDrill
        ? (() => {
            for (let i = drillFrames.length - 2; i >= 0; i--) {
                const f = drillFrames[i];
                if (f.kind === "record") return f.recordRef;
                if (f.kind === "list" && f.recordRef) return f.recordRef;
            }
            return action.recordRef;
        })()
        : undefined;

    // When drilled, we also need the ROOT action's metadata (for breadcrumb
    // label + back-to-list navigation) and the root record's display_name (for
    // the intermediate crumb). Both load only when drill is active.
    const rootLoad = useActionLoad(drillFrame ? action.actionKey : "");
    const rootRecord = useRecord(
        drillFrame ? action.actionKey : "",
        drillFrame ? action.recordRef : undefined,
    );

    const load = useActionLoad(slug);
    const filtersQ = useActionFilters(slug);
    const listPlan = load.data?.listPlan ?? null;
    const kanbanPlan = load.data?.kanbanPlan ?? null;
    const searchPlan = load.data?.searchPlan;
    const modelKey = load.data?.action.modelKey ?? slug;

    // ── list UI state ─────────────────────────────────────────────────────────
    // Session-custom filters: ad-hoc filter rows the user created by free-text
    // searching this session. Stay listed in the Filters card (toggleable like
    // any predefined filter) until page reload. Active membership is tracked
    // via activeFilterKeys, same mechanism as predefined filters.
    const [sessionCustomFilters, setSessionCustomFilters] = useState<SearchFilter[]>([]);
    const [activeFilterKeys, setActiveFilterKeys] = useState<Set<string>>(new Set());
    const adHocCounterRef = useRef(0);
    const createAdHocFilter = (spec: { fieldLabel: string; text: string; domain: Domain }) => {
        // Dedupe by (fieldLabel, text) — typing the same keyword twice for the
        // same field re-activates the existing session filter instead of
        // accumulating an identical row. Behavior parity with hitting the
        // existing Item in the Filters card.
        const existing = sessionCustomFilters.find(
            (f) => f.fieldLabel === spec.fieldLabel && f.text === spec.text,
        );
        if (existing) {
            setActiveFilterKeys((prev) => {
                if (prev.has(existing.key)) return prev;
                const next = new Set(prev);
                next.add(existing.key);
                return next;
            });
            return;
        }
        adHocCounterRef.current += 1;
        const key = `adhoc:${adHocCounterRef.current}`;
        const filter: SearchFilter = {
            key,
            label: `${spec.fieldLabel}: ${spec.text}`,
            domain: JSON.stringify(spec.domain),
            fieldLabel: spec.fieldLabel,
            text: spec.text,
        };
        setSessionCustomFilters((prev) => [...prev, filter]);
        setActiveFilterKeys((prev) => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });
    };
    // Multi-level group-by: ordered list of field names. First entry is the
    // outermost grouping; subsequent entries are nested levels rendered inside
    // each parent bucket.
    const [groupBy, setGroupBy] = useState<string[]>([]);
    // Toggle compares by the bare field name (strips any `:granularity`
    // suffix) so toggling "order_date" off removes "order_date:month" too;
    // the encoded form is preserved exactly as the SearchPanel passed it
    // (date/datetime fields arrive pre-suffixed with their default bucket).
    const toggleGroupBy = (field: string) =>
        setGroupBy((prev) => {
            const bare = groupByEntryField(field);
            const existing = prev.some((e) => groupByEntryField(e) === bare);
            if (existing) return prev.filter((e) => groupByEntryField(e) !== bare);
            return [...prev, field];
        });
    const removeGroupBy = (field: string) =>
        setGroupBy((prev) => prev.filter((e) => groupByEntryField(e) !== groupByEntryField(field)));

    // Session-only "sticky" custom group fields — fields the user picked via
    // the Custom Group dialog stay listed in the Group By section for this
    // session so the user can re-toggle them after removing the chip. Cleared
    // on slug change (action switch) and on page reload (state is in-memory).
    const [sessionCustomGroupFields, setSessionCustomGroupFields] = useState<string[]>([]);
    const addSessionCustomGroupFields = (fields: string[]) =>
        setSessionCustomGroupFields((prev) => {
            const next = new Set(prev);
            fields.forEach((f) => next.add(f));
            return [...next];
        });
    useEffect(() => setSessionCustomGroupFields([]), [slug]);
    const [sortField, setSortField] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
    const [offset, setOffset] = useState(0);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [hiddenCols, setHiddenCols] = useState<Set<string>>(new Set());
    const [foldedCols, setFoldedCols] = useState<Set<string>>(new Set());
    const [deleting, setDeleting] = useState(false);

    const groupbys = searchPlan?.groupbys ?? [];
    const hasActive = searchPlan?.hasActive ?? false;
    // Virtual filter: when the model has an `active` field, surface an
    // "Archived" filter that shows ONLY archived rows (active=false). The
    // explicit `active` leaf in the domain also disables the ORM's auto
    // `active=true` scoping, so only archived records come back. Flowed
    // through the standard SearchFilter pipeline so it gets the same chip /
    // item UX as every other filter.
    const ARCHIVED_FILTER_KEY = "__archived";
    const predefined = useMemo<SearchFilter[]>(() => {
        const base = searchPlan?.filters ?? [];
        if (!hasActive) return base;
        return [
            ...base,
            { key: ARCHIVED_FILTER_KEY, label: "Archived", domain: '[["active","=",false]]' },
        ];
    }, [searchPlan?.filters, hasActive]);
    const favourites: SearchFilter[] = useMemo(
        () =>
            (filtersQ.data ?? [])
                .filter((f) => f.filterType === "favorite")
                .map((f) => ({ key: f.id, label: f.name, domain: f.domain })),
        [filtersQ.data],
    );
    // allFilters combines: DSL predefined + saved favourites + this session's
    // ad-hoc filters (created from free-text search). They all flow through
    // the same activeFilterKeys / domain pipeline.
    const allFilters = useMemo(
        () => [...predefined, ...favourites, ...sessionCustomFilters],
        [predefined, favourites, sessionCustomFilters],
    );

    const filterKey = [...activeFilterKeys].sort().join(",");
    const groupByKey = groupBy.join(",");
    useEffect(() => {
        setOffset(0);
        setSelectedIds(new Set());
    }, [slug, groupByKey, sortField, sortDir, filterKey]);

    // Session-custom filters are scoped to the current action — clear on
     // switch so the section reverts to its predefined-only state.
    useEffect(() => {
        setSessionCustomFilters([]);
        adHocCounterRef.current = 0;
    }, [slug]);

    // On slug switch, also reset the user's group-by stack and remember that
    // this slug's listPlan default has not yet been applied. The next effect
    // below applies it once the listPlan finishes loading.
    const groupByInitialisedFor = useRef<string | null>(null);
    useEffect(() => {
        groupByInitialisedFor.current = null;
        setGroupBy([]);
    }, [slug]);
    useEffect(() => {
        if (!listPlan) return;
        if (groupByInitialisedFor.current === slug) return;
        groupByInitialisedFor.current = slug;
        if (listPlan.defaultGroupBy && listPlan.defaultGroupBy.length > 0) {
            setGroupBy(listPlan.defaultGroupBy);
        }
    }, [slug, listPlan]);

    // Default column visibility from the list DSL: optional="hide" columns start
    // hidden; everything else visible. User toggles (below) override per session.
    useEffect(() => {
        if (!listPlan) return;
        setHiddenCols(new Set(listPlan.columns.filter((c) => c.optional === "hide").map((c) => c.name)));
    }, [slug, listPlan]);

    const domainLeaves = useMemo<Domain>(() => {
        const d: Domain = [];
        for (const f of allFilters) {
            if (!activeFilterKeys.has(f.key) || !f.domain) continue;
            try {
                const parsed = JSON.parse(f.domain);
                if (Array.isArray(parsed)) d.push(...(parsed as Domain));
            } catch {
                /* ignore malformed filter domain */
            }
        }
        return d;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allFilters, filterKey]);

    // List-drill base domain (Enhancement 06). When the URL parks us on a
    // ListDrillFrame, the bespoke action's `domain` template carries
    // placeholders that resolve against the upstream record:
    //   - `active_id`                — the parent's record_uuid
    //   - `$active_record.<field>`   — JSON-serialized field value (M2M arrays
    //                                  expand to a list of UUIDs; refs collapse
    //                                  to their UUID). Required for M2M chips
    //                                  whose target model has no inverse field.
    // The substituted result is concatenated AT THE FRONT of `domainLeaves`
    // so user-applied filters apply on top.
    const drillDomain = useMemo<Domain>(() => {
        if (!listDrill) return [];
        const template = load.data?.action.domain;
        if (!template) return [];
        const withActiveId = substituteActiveId(template, activeId ?? "");
        const fullySubstituted = substituteActiveRecord(
            withActiveId,
            (rootRecord.data ?? null) as Record<string, unknown> | null,
        );
        try {
            const parsed = JSON.parse(fullySubstituted);
            return Array.isArray(parsed) ? (parsed as Domain) : [];
        } catch {
            return [];
        }
    }, [listDrill, load.data?.action.domain, activeId, rootRecord.data]);

    const query = useMemo<ListQuery>(() => {
        // When grouping, order by each group field first so records arrive in
        // bucket-contiguous order; then by the user's sort (if any). Otherwise
        // sort wins, falling back to the list DSL's order_by.
        const groupOrders = groupBy.map((f) => `${f} asc`);
        const sortOrder = sortField ? `${sortField} ${sortDir}` : null;
        const order =
            groupOrders.length > 0
                ? [...groupOrders, ...(sortOrder ? [sortOrder] : [])].join(", ")
                : sortOrder ?? listPlan?.orderBy;
        const combined = [...drillDomain, ...domainLeaves];
        return {
            limit: PAGE_SIZE,
            offset,
            order,
            domain: combined.length ? combined : undefined,
            // Server-grouped fetch: the backend enumerates EVERY group (enum /
            // reference axes expand to all possible values, empty included)
            // with true counts and a page of records per group. Date-bucketed
            // axes fall back to the flat page server-side; the client buckets
            // those from the contiguous order above.
            groupby: groupBy.length > 0 ? groupBy : undefined,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, sortField, sortDir, groupByKey, listPlan?.orderBy, domainLeaves, drillDomain]);

    const isRecord = Boolean(recordRef);
    const isList = !isRecord && viewMode === "list";
    const isKanban = !isRecord && viewMode === "kanban";
    // Kanban is single-axis. User-applied groupBy wins; otherwise fall back to
    // the DSL's default_group_by; if neither, the board renders as a flat card
    // grid. Clearing the user's group-by reverts to the DSL default (if any).
    const activeKanbanAxis: string | null = isKanban
        ? groupBy[0] ?? kanbanPlan?.defaultGroupBy ?? null
        : null;
    // Ungrouped kanban is a paged card grid, so it shows the pager; a grouped
    // board does not (columns hold their own groups).
    const kanbanUngrouped = isKanban && Boolean(kanbanPlan) && !activeKanbanAxis;
    // Keep the surrounding-rows query alive in form mode too — the form-toolbar
    // pager resolves the current record's neighbour by index in `rows`, so the
    // query needs to have fired (TanStack will hit cache on subsequent record
    // navigations within the same action).
    const records = useActionRecords(
        slug,
        query,
        (isList || isKanban || isRecord) && Boolean(listPlan || kanbanPlan),
    );
    const recordQ = useRecord(slug, recordRef);

    // ── Assistant binding publication ──────────────────────────────────────
    // The AssistantPanel reads this binding to (a) decide contextual vs
    // global mode, (b) personalize its greeting with display_name, and
    // (c) dispatch view-intent ops back into the action's state. Updated
    // every time any of the bound fields changes; cleared on unmount so
    // the panel reverts to global mode when the user leaves the action.
    const { setBinding, clearBinding } = useAssistantBindingSetter();

    const recordDisplayName = useMemo(() => {
        const rec = recordQ.data as Record<string, unknown> | undefined;
        if (!rec) return null;
        const dn = rec.display_name ?? rec.name ?? rec.title;
        return typeof dn === "string" && dn.length > 0 ? dn : null;
    }, [recordQ.data]);

    // Stabilize applyOps via a ref so its identity doesn't churn across
    // renders — `createAdHocFilter` is a plain inline arrow in ActionManager
    // (recreated every render), and including it in useCallback deps would
    // make applyOps a new function on every render → the setBinding
    // useEffect would re-fire each time → BindingProvider re-renders →
    // ActionManager re-renders → infinite loop ("Maximum update depth
    // exceeded"). The ref pattern reads the latest values without
    // participating in dep-comparison.
    const applyOpsDepsRef = useRef({ createAdHocFilter, dispatch, slug, modelKey });
    useEffect(() => {
        applyOpsDepsRef.current = { createAdHocFilter, dispatch, slug, modelKey };
    });

    const applyOps = useCallback(
        (ops: ViewIntentOp[], onApplied?: (count: number) => void): void => {
            const { createAdHocFilter, dispatch, slug, modelKey } = applyOpsDepsRef.current;
            let applied = 0;
            for (const op of ops) {
                const payload = (op.payload ?? {}) as Record<string, unknown>;
                switch (op.op) {
                    case "apply_filter": {
                        const dom = payload.domain;
                        const fieldLabel = (payload.label as string | undefined) ?? (payload.field as string | undefined) ?? "Assistant";
                        const text = (payload.text as string | undefined) ?? (typeof dom === "string" ? dom : JSON.stringify(dom));
                        if (dom !== undefined) {
                            try {
                                createAdHocFilter({
                                    fieldLabel,
                                    text,
                                    domain: (typeof dom === "string" ? JSON.parse(dom) : dom) as Domain,
                                });
                                applied++;
                            } catch {
                                // malformed payload — skip silently
                            }
                        }
                        break;
                    }
                    case "set_groupby": {
                        const fields = payload.fields as string[] | undefined;
                        const field = payload.field as string | undefined;
                        if (Array.isArray(fields) && fields.length > 0) {
                            setGroupBy(fields);
                            applied++;
                        } else if (field) {
                            setGroupBy([field]);
                            applied++;
                        }
                        break;
                    }
                    case "set_sort": {
                        const field = payload.field as string | undefined;
                        const direction = (payload.direction as "asc" | "desc" | undefined) ?? "asc";
                        if (field) {
                            setSortField(field);
                            setSortDir(direction);
                            applied++;
                        }
                        break;
                    }
                    case "clear_filters": {
                        setActiveFilterKeys(new Set());
                        setSessionCustomFilters([]);
                        applied++;
                        break;
                    }
                    case "switch_view": {
                        const viewType = payload.view_type as string | undefined;
                        if (viewType && (viewType === "list" || viewType === "kanban" || viewType === "form" || viewType === "calendar")) {
                            dispatch(command("OPEN_ACTION", { actionKey: slug, viewMode: viewType }));
                            applied++;
                        }
                        break;
                    }
                    case "open_record": {
                        const recordId = (payload.record_id as string | undefined) ?? (payload.record_uuid as string | undefined);
                        const targetModel = (payload.model_key as string | undefined) ?? modelKey;
                        if (recordId) {
                            dispatch(command("OPEN_RECORD", { recordRef: recordId, modelKey: targetModel }));
                            applied++;
                        }
                        break;
                    }
                    case "show_columns": {
                        // Deferred — show_columns needs the full column inventory
                        // from listPlan to invert into hiddenCols. Slice 4 polish.
                        break;
                    }
                }
            }
            onApplied?.(applied);
        },
        // Empty deps — applyOps identity stays stable across renders.
        // Fresh values are read from applyOpsDepsRef.current on every call.
        [],
    );

    // Publish the binding on every relevant state change.
    useEffect(() => {
        if (!slug || !modelKey) {
            clearBinding();
            return;
        }
        // Phase 4C — surface the active view's id + primary ir.application.view
        // uuid so the assistant confirm card can author a user extension
        // (parent_id) against the open view.
        const activePlan =
            viewMode === "form"
                ? load.data?.formPlan ?? null
                : viewMode === "kanban"
                  ? kanbanPlan
                  : listPlan;
        setBinding({
            anchoredModelKey: modelKey,
            anchoredActionKey: slug,
            viewType: viewMode === "list" || viewMode === "form" || viewMode === "kanban" || viewMode === "calendar"
                ? viewMode
                : null,
            viewState: buildViewState({
                groupBy,
                sortField,
                sortDir,
            }),
            recordRef: recordRef ?? null,
            recordDisplayName,
            viewKey: activePlan?.viewId ?? null,
            viewParentId: activePlan?.viewParentId ?? null,
            applyOps,
        });
    }, [
        slug,
        modelKey,
        viewMode,
        groupBy,
        sortField,
        sortDir,
        recordRef,
        recordDisplayName,
        applyOps,
        setBinding,
        listPlan,
        kanbanPlan,
        load.data?.formPlan,
        clearBinding,
    ]);

    // Clear binding on unmount so the panel reverts to global mode when the
    // user leaves the workspace action area entirely.
    useEffect(() => {
        return () => clearBinding();
    }, [clearBinding]);

    // Print menu lister — /api/reports/_list per (model, mode, record). The
    // hook short-circuits when modelKey or recordId is missing, so deep links
    // and new-record drafts skip the fetch.
    const reportFormRef = isRecord && recordRef && recordRef !== "new" ? recordRef : undefined;
    const reportQ = useReportActions({
        modelKey,
        mode: isRecord ? "form" : "list",
        recordId: reportFormRef,
        enabled: isRecord && Boolean(reportFormRef),
    });
    const reportActions = reportQ.data ?? [];

    // Selection print lister — same endpoint, mode="list", target = selected
    // record ids. Fired only when selection bar is visible.
    const selectionReportQ = useReportActions({
        modelKey,
        mode: "list",
        enabled: isList && selectedIds.size > 0,
    });
    const selectionReportActions = selectionReportQ.data ?? [];

    // Server-grouped response: groups[] carries the data (records[] is empty).
    // Flatten for every consumer that wants a row list (selection, form pager,
    // kanban cards) — each group already holds up to a page of its records.
    const serverGroups = records.data?.grouped ? records.data.groups ?? [] : null;
    const rows = serverGroups
        ? serverGroups.flatMap((g) => g.records)
        : records.data?.records ?? [];
    const total = records.data?.total;
    // Pager hides when there's nothing to page through — and in grouped mode,
    // where pagination is per-group (each group ships its own first page).
    const pagerOn = ((isList && !serverGroups) || kanbanUngrouped) && rows.length > 0;

    // Action-switch transition gate. True while:
    //  - the action plan is being fetched for the current slug, OR
    //  - the records query is still serving the previous slug's rows as
    //    placeholder data while the new slug's rows are in flight.
    // When true, the body renders <ViewLoader /> instead of the stale view
    // — kills the "old data flashes, then re-renders" glitch on action click.
    const planLoading = load.isPending || load.isLoading;
    const rowsTransitioning = (isList || isKanban) && records.isFetching && records.isPlaceholderData;
    const transitioning = planLoading || rowsTransitioning;
    const fieldDefs = load.data?.fieldDefs ?? [];

    // Kanban columns: every possible group value (enum options / all related
    // records), so empty stages still show. Falls back to data-derived columns.
    const groupField = activeKanbanAxis ?? "";
    const groupFieldDef = fieldDefs.find((f) => f.name === groupField);
    const isRefGroup = groupFieldDef?.type === "reference";
    const refOptionsQ = useGroupOptions(slug, groupField, isKanban && isRefGroup);
    const groupOptions = useMemo<GroupOption[] | undefined>(() => {
        if (!groupField) return undefined;
        if (groupFieldDef?.type === "enum" && groupFieldDef.selection) {
            return groupFieldDef.selection.map(([value, label]) => ({ value, label }));
        }
        if (isRefGroup) {
            return (refOptionsQ.data ?? []).map((r) => {
                const data = (r.data && typeof r.data === "object" ? (r.data as Record<string, Json>) : r) as Record<string, Json>;
                const fold = data.fold ?? data.folded;
                return {
                    value: String(r.id),
                    label: String(r.display_name ?? r.id),
                    color: data.color,
                    fold: fold === true,
                };
            });
        }
        return undefined;
    }, [groupField, groupFieldDef, isRefGroup, refOptionsQ.data]);

    // Default column folding: a reference stage marked fold, OR (reference only)
    // any column past the 10th, starts collapsed. User can re-expand after.
    useEffect(() => {
        if (!groupField || !groupOptions) return;
        const folded = new Set<string>();
        groupOptions.forEach((o, i) => {
            if (o.fold || (isRefGroup && i >= REFERENCE_COLUMN_LIMIT)) folded.add(String(o.value));
        });
        setFoldedCols(folded);
    }, [slug, groupField, isRefGroup, groupOptions]);

    // ── breadcrumb / navigation ───────────────────────────────────────────────
    const actionName = load.data?.action.name ?? slug;
    const backToList = () => void dispatch(command("OPEN_ACTION", { actionKey: slug, viewMode }));
    const recordName =
        recordRef === "new"
            ? "New record"
            : recordQ.data?.display_name != null
              ? String(recordQ.data.display_name)
              : (recordRef ?? "");
    const crumbs: Crumb[] = [];
    if (drillFrame) {
        // Drill-mode breadcrumb chain: root-action → root-record → … → drill.
        // Title (rendered separately by FormControlPanel) is the active drill
        // record's display_name, so we DON'T push a crumb for it here.
        const rootActionName = rootLoad.data?.action.name ?? action.actionKey;
        crumbs.push({
            label: rootActionName,
            onClick: () => navigateTo(buildWorkspacePath(orgCode, [
                { kind: "action", actionKey: action.actionKey, viewMode: action.viewMode },
            ])),
        });
        if (action.recordRef) {
            const rootRecordName = rootRecord.data?.display_name != null
                ? String(rootRecord.data.display_name)
                : action.recordRef;
            crumbs.push({
                label: rootRecordName,
                onClick: () => navigateTo(buildWorkspacePath(orgCode, [
                    { kind: "action", actionKey: action.actionKey, viewMode: action.viewMode, recordRef: action.recordRef },
                ])),
            });
        }
        // Intermediate drill frames (everything between the root and the last
        // drill frame). Record frames still fall back to uuid-prefix when no
        // display_name is reachable (lookup would require a hook-per-frame —
        // typical drill depth is 1–2 so the common path doesn't hit this).
        // List frames with their own recordRef get a 2-crumb expansion: the
        // action name (pointing at the list with recordRef cleared) followed
        // by the record's identity in the trailing form crumb (handled by
        // the title path below for the LAST frame; intermediates use the
        // recordRef slice as a placeholder).
        drillFrames.slice(0, -1).forEach((frame, idx) => {
            const stackToHere: import("../core").NavigationStack = [stack[0], ...drillFrames.slice(0, idx + 1)];
            const fallback = frame.kind === "record"
                ? frame.recordRef.slice(0, 8)
                : frame.actionSlug;
            crumbs.push({
                label: fallback,
                onClick: () => navigateTo(buildWorkspacePath(orgCode, stackToHere)),
            });
        });
        // When the LAST drill frame is a list with a recordRef (a row opened
        // inside a drilled list), push the list-action's name as an extra
        // crumb so the chain reads `root → root.record → ListName → ⟨title⟩`.
        // The title (handled below) renders the open record's display_name.
        // Clicking the ListName crumb pops back to the list (recordRef cleared
        // on the top frame).
        if (listDrill && listDrill.recordRef) {
            const listName = load.data?.action.name ?? listDrill.actionSlug;
            const stackToList: import("../core").NavigationStack = [
                stack[0],
                ...drillFrames.slice(0, -1),
                { ...listDrill, recordRef: undefined },
            ];
            crumbs.push({
                label: listName,
                onClick: () => navigateTo(buildWorkspacePath(orgCode, stackToList)),
            });
        }
    } else if (isRecord) {
        crumbs.push({ label: actionName, onClick: backToList });
    }
    // Title resolution:
    //   - list-drill WITH recordRef → drilled record's display_name (recordQ)
    //   - list-drill WITHOUT recordRef → action.name (the list view title)
    //   - record-drill → record display_name (resolved via recordQ)
    //   - root record → recordName
    //   - root list → actionName
    const title = listDrill
        ? (listDrill.recordRef
              ? (recordQ.data?.display_name != null ? String(recordQ.data.display_name) : listDrill.recordRef)
              : (load.data?.action.name ?? listDrill.actionSlug))
        : recordDrill
            ? (recordQ.data?.display_name != null ? String(recordQ.data.display_name) : recordRef ?? "")
            : isRecord
                ? recordName
                : actionName;

    // ── handlers ──────────────────────────────────────────────────────────────
    const toggleRow = (id: string) =>
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    const toggleAll = () =>
        setSelectedIds((prev) => {
            const ids = rows.map((r) => String(r.id ?? r.record_uuid));
            const allOn = ids.length > 0 && ids.every((id) => prev.has(id));
            const next = new Set(prev);
            for (const id of ids) {
                if (allOn) next.delete(id);
                else next.add(id);
            }
            return next;
        });
    const onSort = (field: string) => {
        if (sortField !== field) {
            setSortField(field);
            setSortDir("asc");
        } else if (sortDir === "asc") {
            setSortDir("desc");
        } else {
            setSortField(null);
            setSortDir("asc");
        }
    };
    const toggleColumn = (name: string) =>
        setHiddenCols((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    const toggleFold = (id: string) =>
        setFoldedCols((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    const refetchRecords = () => queryClient.invalidateQueries({ queryKey: ["action-records", slug] });

    // Remember where "New" was launched from so discarding the fresh draft
    // returns there. Clicking New on an existing record's form should drop
    // back to THAT record on discard; clicking New from the list/kanban (no
    // open record) falls back to the list. The ref survives the navigation
    // because ActionManager stays mounted (only RecordProvider remounts).
    const newOriginRef = useRef<string | null>(null);
    const onCreate = (_groupValue?: Json | null) => {
        newOriginRef.current = recordRef && recordRef !== "new" ? recordRef : null;
        void dispatch(command("CREATE_RECORD", { modelKey }));
    };
    const onDiscardNew = () => {
        const origin = newOriginRef.current;
        newOriginRef.current = null;
        if (origin) void dispatch(command("OPEN_RECORD", { recordRef: origin }));
        else backToList();
    };
    const onOpenRecord = (id: string) => void dispatch(command("OPEN_RECORD", { recordRef: id }));

    // Inline quick-create from a kanban column foot — directly creates a
    // record pre-filled with the typed name AND the active groupby axis set
    // to the column's group value. Skips the full form view entirely; the
    // user stays in the board flow. Falls back when no axis is active (rare).
    const onQuickCreate = async (groupValue: Json | null, values: Record<string, Json>) => {
        const merged: Record<string, Json> = { ...values };
        if (activeKanbanAxis) merged[activeKanbanAxis] = groupValue;
        await runtime.services.record.create(modelKey, merged as Json);
        await refetchRecords();
    };

    const onBulkDelete = async () => {
        const ids = [...selectedIds];
        if (!ids.length) return;
        const ok = await confirmDialog({
            title: ids.length === 1 ? "Delete record?" : `Delete ${ids.length} records?`,
            message:
                ids.length === 1
                    ? "This record will be permanently removed. This action can't be undone."
                    : `${ids.length} selected records will be permanently removed. This action can't be undone.`,
            confirmLabel: ids.length === 1 ? "Delete" : `Delete ${ids.length}`,
            cancelLabel: "Cancel",
            variant: "danger",
        });
        if (!ok) return;
        setDeleting(true);
        try {
            for (const id of ids) await runtime.services.record.delete(modelKey, id);
            setSelectedIds(new Set());
            await refetchRecords();
            // Same cascade-aware broad invalidation as onDeleteCurrent above.
            queryClient.invalidateQueries({ queryKey: ["record"] });
        } finally {
            setDeleting(false);
        }
    };

    // Bulk archive — only meaningful when the model has an `active` field
    // (see `hasActive` from the search plan). Sets `active=false` per
    // selected id and refetches; the ORM's default active-filter then hides
    // the archived rows from the current list.
    const [archiving, setArchiving] = useState(false);
    const onBulkArchive = async () => {
        const ids = [...selectedIds];
        if (!ids.length || !hasActive) return;
        const ok = await confirmDialog({
            title: ids.length === 1 ? "Archive record?" : `Archive ${ids.length} records?`,
            message:
                ids.length === 1
                    ? "This record will be archived. You can still find it via the Archived filter."
                    : `${ids.length} selected records will be archived. You can still find them via the Archived filter.`,
            confirmLabel: ids.length === 1 ? "Archive" : `Archive ${ids.length}`,
            cancelLabel: "Cancel",
        });
        if (!ok) return;
        setArchiving(true);
        try {
            for (const id of ids) await runtime.services.record.update(modelKey, id, { active: false });
            setSelectedIds(new Set());
            await refetchRecords();
        } finally {
            setArchiving(false);
        }
    };

    // Drop-card handler with dual-mode dispatch. `field` mode writes the
    // groupby axis directly via record.update — simple stage moves on plain
    // status fields. `workflow` mode looks up the matching `ir.workflow.
    // transition` and runs it via the workflow service, so guards, approvals,
    // and lifecycle hooks fire end-to-end. KanbanView resolves the mode from
    // `plan.onDrag` before calling us; this side just executes.
    const onDropCard = async (id: string, groupValue: Json | null, mode: "field" | "workflow") => {
        if (!activeKanbanAxis) return;
        if (mode === "workflow") {
            await runtime.services.workflow.transitionToStage(modelKey, id, groupValue);
        } else {
            await runtime.services.record.update(modelKey, id, { [activeKanbanAxis]: groupValue });
        }
        await refetchRecords();
    };

    // Generic inline single-field write from a list cell — used by the boolean
    // toggle widget today and (Phase 4) any inline-edit widget. Writes one
    // field on one record via record.update and refetches. No confirm dialog
    // (cheap single-record op; user can re-toggle / re-edit to undo).
    const onWriteField = async (record: RecordData, fieldName: string, value: unknown) => {
        const recordUuid = String(record.id ?? record.record_uuid ?? "");
        if (!recordUuid) return;
        await runtime.services.record.update(modelKey, recordUuid, { [fieldName]: value as Json });
        await refetchRecords();
    };

    // Button dispatch is now uniformly the platform RUN_BUTTON command — same
    // bus, same middleware (pendingTrack + error→toast), same engine registry
    // (workflow / approval / report / default with canHandle fallback). Views
    // call `useCommandDispatch("RUN_BUTTON", payload)` directly for per-button
    // spinner + disabled state. ActionManager keeps these thin adapter
    // callbacks ONLY to translate from the per-view button shape to RUN_BUTTON
    // payload and to refetch the list after the bus settles.
    const onRunFormButton = async (button: FormButtonElement) => {
        const recordUuid = String(recordRef ?? "");
        if (!recordUuid || recordUuid === "new" || !button.command) return;
        // `special=` short-circuit (Enhancement-06-era WS 4.5) — interrupt
        // BEFORE engine dispatch. Handler returns extra context to merge
        // into the command (e.g. file_upload returns {file_content, …}),
        // or null when the user cancelled mid-flow.
        let mergedContext: Record<string, Json> = button.context
            ? (safeParseJson(button.context) as Record<string, Json>) ?? {}
            : {};
        if (button.special) {
            const handler = resolveSpecial(button.special);
            if (handler) {
                const extra = await handler.execute(button.specialOptions ?? {});
                if (extra === null) return;  // user cancelled
                mergedContext = { ...mergedContext, ...extra };
            }
        }
        await dispatch(command("RUN_BUTTON", {
            actionSlug: slug,
            recordUuid,
            commandName: button.command,
            commandType: button.commandType ?? null,
            label: button.label ?? button.name,
            context: mergedContext as Json,
        }));
        await refetchRecords();
    };

    // Dynamic workflow transition pills — auto-fetched from /api/workflow/available.
    // Synthesises a RUN_BUTTON dispatch with commandType="workflow" so it flows
    // through the workflow engine (which requires modelKey in context). The
    // `name` is the human-readable transition label ("Convert to Lead") that
    // the success toast surfaces — not the wire code (`convert_to_lead`).
    const onRunFormTransition = async (code: string, name?: string) => {
        const recordUuid = String(recordRef ?? "");
        if (!recordUuid || recordUuid === "new" || !modelKey) return;
        await dispatch(command("RUN_BUTTON", {
            actionSlug: slug,
            recordUuid,
            commandName: code,
            commandType: "workflow",
            label: name ?? code,
            context: { modelKey } as Json,
        }));
        await refetchRecords();
        await queryClient.invalidateQueries({ queryKey: ["workflow-transitions", modelKey, recordUuid] });
    };

    const onRowButton = async (button: ListRowButton, record: RecordData) => {
        const recordUuid = String(record.id ?? record.record_uuid ?? "");
        if (!recordUuid || !button.command) return;
        await dispatch(command("RUN_BUTTON", {
            actionSlug: slug,
            recordUuid,
            commandName: button.command,
            commandType: button.commandType ?? null,
            label: button.label ?? button.name,
            context: button.context ? safeParseJson(button.context) : null,
        }));
        await refetchRecords();
    };

    // ── form-mode helpers ─────────────────────────────────────────────────
    // Record-pager: when viewing a record, find its index in the surrounding
    // list (if loaded) so the form toolbar can show "N / total" + prev/next.
    const recIdxInList = isRecord && recordRef && recordRef !== "new"
        ? rows.findIndex((r) => String(r.id ?? r.record_uuid) === recordRef)
        : -1;
    // Lazy-page navigation: when prev/next steps OUT of the current in-memory
    // page (i.e. the user is on the first row and wants prev, or on the last
    // row of page N and wants next), we bump `offset` and stash a target
    // ("first" or "last" of the new page) so the effect below picks it up
    // once the refetch lands.
    const [pendingPagerNav, setPendingPagerNav] = useState<"first" | "last" | null>(null);
    // Form pager is visible whenever this record is part of the loaded list
    // OR there's a known total telling us additional pages may exist. When
    // the user is on a record that isn't in `rows` but `total` exists, the
    // pager renders disabled-but-present rather than disappearing (cleaner
    // affordance during the lazy-fetch window).
    const formPagerVisible = recIdxInList >= 0 || (isRecord && (total ?? 0) > 0);
    const navigateToRecord = (id: string) => void dispatch(command("OPEN_RECORD", { recordRef: id }));
    const onFormPrev = () => {
        if (recIdxInList > 0) {
            const prev = rows[recIdxInList - 1];
            if (prev) navigateToRecord(String(prev.id ?? prev.record_uuid));
            return;
        }
        // At index 0 (or off-page) — step back a page if one exists.
        if (offset > 0) {
            setOffset((o) => Math.max(0, o - PAGE_SIZE));
            setPendingPagerNav("last");
        }
    };
    const onFormNext = () => {
        if (recIdxInList >= 0 && recIdxInList < rows.length - 1) {
            const next = rows[recIdxInList + 1];
            if (next) navigateToRecord(String(next.id ?? next.record_uuid));
            return;
        }
        // At the end of the current page — step forward if more records exist
        // beyond the loaded window.
        if (total != null && offset + rows.length < total) {
            setOffset((o) => o + PAGE_SIZE);
            setPendingPagerNav("first");
        }
    };
    // After a lazy page-step, when the new page lands, navigate to the target
    // edge (first row for next, last row for prev). Clears the pending flag
    // so a manual offset change doesn't re-trigger.
    useEffect(() => {
        if (!pendingPagerNav || rows.length === 0) return;
        const target = pendingPagerNav === "first" ? rows[0] : rows[rows.length - 1];
        if (!target) return;
        const id = String(target.id ?? target.record_uuid ?? "");
        if (id) navigateToRecord(id);
        setPendingPagerNav(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingPagerNav, rows]);
    // Print menu items for the current record — dispatch each registered
     // report via /api/reports/<key>/run and deliver the result (pdf → new
     // tab, docx/xlsx → download, etc.). Empty when no reports are registered.
    // Enh 10 — record-bound server actions for this model, shown in the form's
    // Actions ▾ below the standard items. Only on a saved record (a bound
    // wizard like Change Password needs an existing target). Each opens the
    // action as a dialog with the active record substituted into its context.
    const customActionItems: ActionItem[] =
        recordRef && recordRef !== "new"
            ? (load.data?.boundActions ?? []).map((ba) => ({
                key: ba.slug,
                label: ba.name,
                onSelect: () => dialogStack.push({
                    actionSlug: ba.slug,
                    recordRef: "new",
                    defaults: buildBoundActionDefaults(
                        ba.context,
                        recordRef as string,
                        recordQ.data?.display_name != null ? String(recordQ.data.display_name) : undefined,
                    ),
                    onSaved: buildSelfLogoutHandler(ba.context, session.userId, () => {
                        void runtime.services.auth.logout().finally(() =>
                            navigate({ to: "/auth/login" } as Parameters<typeof navigate>[0]));
                    }),
                }),
            }))
            : [];
    const customPrintItems: PrintItem[] = reportActions.map((d) => ({
        key: d.action_key,
        label: d.name,
        onSelect: () => {
            const format = d.available_formats[0] ?? "pdf";
            const target = reportFormRef ? [reportFormRef] : [];
            void runtime.services.report
                .run(d.action_key, { target_record_uuids: target, format, persist: false })
                .then((res) => deliverReport(res, d.name))
                .catch((exc) => {
                    // eslint-disable-next-line no-console
                    console.error("[Report] dispatch failed:", d.action_key, exc);
                });
        },
    }));

    // Same shape but for the selection bar — target is the current selection,
    // not the form's record.
    const selectionPrintItems: PrintItem[] = selectionReportActions.map((d) => ({
        key: d.action_key,
        label: d.name,
        onSelect: () => {
            const format = d.available_formats[0] ?? "pdf";
            const target = [...selectedIds];
            void runtime.services.report
                .run(d.action_key, { target_record_uuids: target, format, persist: false })
                .then((res) => deliverReport(res, d.name))
                .catch((exc) => {
                    // eslint-disable-next-line no-console
                    console.error("[Report] dispatch failed:", d.action_key, exc);
                });
        },
    }));

    const onDeleteCurrent = async () => {
        if (!recordRef || recordRef === "new") return;
        const ok = await confirmDialog({
            title: "Delete record?",
            message: "This record will be permanently removed. This action can't be undone.",
            confirmLabel: "Delete",
            cancelLabel: "Cancel",
            variant: "danger",
        });
        if (!ok) return;
        await runtime.services.record.delete(modelKey, recordRef);
        await refetchRecords();
        // Broad invalidate: deleting a record often cascades into related
        // records via on_delete="set null" FK rules (e.g. crm.lead.delete
        // clears crm.inquiry.converted_lead_id). We can't predict the cascade
        // client-side, so we mark every open record query stale; TanStack
        // refetches on remount/reactivate. Without this, the global
        // `staleTime: 30s` on the QueryClient hides the cascade until reload.
        queryClient.invalidateQueries({ queryKey: ["record"] });
        // Where to land after delete:
        //   - Drill mode → pop the deleted record off the drill stack so the
        //     user returns to the parent record (e.g. delete the lead while
        //     drilled, land back on the inquiry). `popFrame` is the canonical
        //     navigation primitive for this.
        //   - Non-drill mode → bounce to the root action's list/kanban — the
        //     record is gone, there's nothing to display in place.
        if (drillFrame) {
            navigateTo(buildWorkspacePath(orgCode, popFrame(stack)));
        } else {
            void dispatch(command("OPEN_ACTION", { actionKey: action.actionKey, viewMode: action.viewMode }));
        }
    };

    const onSaveCurrent = (name: string) => {
        void runtime.services.action
            .createFilter(slug, { name, domain: JSON.stringify(domainLeaves), groupby: groupBy.length > 0 ? groupBy.join(",") : undefined })
            .then(() => queryClient.invalidateQueries({ queryKey: ["filters", slug] }));
    };
    const onDeleteSaved = (key: string) => {
        setActiveFilterKeys((prev) => {
            const next = new Set(prev);
            next.delete(key);
            return next;
        });
        void runtime.services.action
            .deleteFilter(slug, key)
            .then(() => queryClient.invalidateQueries({ queryKey: ["filters", slug] }));
    };

    if (load.isError) {
        return (
            <div className="action-view">
                <div className="list-view">
                    <div className="list-view__state">Failed to load this action.</div>
                </div>
            </div>
        );
    }

    // Orphan drill — drilled into a model whose menu carries no action. Until
    // a model-keyed view lookup ships (WS 4.7 follow-up), show a clear info
    // panel with a fallback back-link to the closest action root.
    if (isOrphanDrill && drillFrame) {
        const backToRoot = () => navigateTo(buildWorkspacePath(orgCode, [
            { kind: "action", actionKey: action.actionKey, viewMode: action.viewMode, recordRef: action.recordRef },
        ]));
        return (
            <div className="action-view">
                <div className="list-view">
                    <div className="list-view__state">
                        <p>No menu action is registered for model <code>{recordDrill?.modelKey}</code>.</p>
                        <p>Record <code>{recordDrill?.recordRef}</code> exists in the database but cannot be opened until a view is registered for this model.</p>
                        <button type="button" className="btn" onClick={backToRoot}>Back</button>
                    </div>
                </div>
            </div>
        );
    }

    const isMulti = isList || isKanban;
    const center = !isMulti
        ? undefined
        : isList && selectedIds.size > 0
            ? <SelectionActions
                count={selectedIds.size}
                onClear={() => setSelectedIds(new Set())}
                onDelete={() => void onBulkDelete()}
                deleting={deleting}
                onArchive={hasActive ? () => void onBulkArchive() : undefined}
                archiving={archiving}
                printItems={selectionPrintItems}
            />
            : (
                  <SearchPanel
                      searchfields={searchPlan?.searchfields ?? []}
                      fieldDefs={load.data?.fieldDefs ?? []}
                      filters={predefined}
                      activeFilterKeys={activeFilterKeys}
                      onToggleFilter={(key) =>
                          setActiveFilterKeys((prev) => {
                              const next = new Set(prev);
                              if (next.has(key)) next.delete(key);
                              else next.add(key);
                              return next;
                          })
                      }
                      groupbys={groupbys}
                      groupBy={groupBy}
                      onToggleGroupBy={toggleGroupBy}
                      onRemoveGroupBy={removeGroupBy}
                      onSetGroupBy={setGroupBy}
                      sessionCustomGroupFields={sessionCustomGroupFields}
                      onAddSessionCustomGroupFields={addSessionCustomGroupFields}
                      sessionCustomFilters={sessionCustomFilters}
                      onCreateAdHocFilter={createAdHocFilter}
                      savedFilters={favourites}
                      onDeleteSaved={onDeleteSaved}
                      onSaveCurrent={onSaveCurrent}
                      canSave={domainLeaves.length > 0 || groupBy.length > 0}
                  />
              );

    // Client-action short-circuit — when the loaded action carries a
    // `clientComponent` key, mount it via ClientActionPage (the
    // unified host: registry lookup + param validation + lifecycle
    // context + Suspense + permission gate). No toolbar, no breadcrumb
    // (the client component owns its chrome). Falls through to the
    // standard render when no client component is declared.
    const clientComponentKey = load.data?.action.clientComponent ?? null;
    if (clientComponentKey) {
        return (
            <div className="action-view action-view--client">
                <ClientActionPage
                    action={load.data!.action}
                    param={recordRef ?? undefined}
                    mountedAs="page"
                />
            </div>
        );
    }

    return (
        <div className="action-view">
            {isRecord ? (
                // WS 4.1 substrate hoisted to wrap BOTH the toolbar and the
                // form body so FormControlPanel can read the edit-cycle state
                // (isEditing / isDirty / onEdit / onSave / onDiscard) via
                // `useFormStateContext`. Provider remounts on record-uuid
                // change so the draft state resets cleanly across URLs.
                <RecordProvider
                    key={recordRef ?? "new"}
                    record={recordQ.data ?? null}
                    isNew={recordRef === "new"}
                    fieldDefs={fieldDefs}
                    viewFieldNames={load.data?.viewFieldNames?.form ?? null}
                    recordRef={recordRef === "new" ? undefined : recordRef}
                    formElements={load.data?.formPlan?.sheet ?? undefined}
                    onCreate={async (values) => {
                        const saved = await runtime.services.action.createRecord(modelKey, values);
                        // The URL still reads `…/new` (CREATE_RECORD set the
                        // root frame's recordRef to "new"). Now that the record
                        // is persisted, swap that segment for the real uuid so
                        // the form leaves draft state and a refresh / deep-link
                        // lands on the saved record, not a blank /new draft.
                        const newId = typeof saved?.record_uuid === "string" ? saved.record_uuid : null;
                        if (newId) onOpenRecord(newId);
                        return saved;
                    }}
                    onUpdate={(uuid, values) => runtime.services.action.updateRecord(modelKey, uuid, values)}
                >
                    <FormControlPanel
                        crumbs={crumbs}
                        title={title}
                        onCreate={onCreate}
                        onDiscardNew={onDiscardNew}
                        pagerVisible={formPagerVisible}
                        pagerIndex={recIdxInList >= 0 ? recIdxInList + 1 + offset : undefined}
                        pagerTotal={total}
                        onPrev={onFormPrev}
                        onNext={onFormNext}
                        canPrev={recIdxInList > 0 || offset > 0}
                        canNext={
                            (recIdxInList >= 0 && recIdxInList < rows.length - 1)
                            || (total != null && offset + rows.length < total)
                        }
                        customPrintItems={customPrintItems}
                        customActionItems={customActionItems}
                        // Duplicate is listed (matching old frontend) but doesn't have a
                        // wired backend command yet — surface it as a no-op so the menu
                        // shape matches; real wiring lands with Phase 4 form-actions.
                        onDuplicate={
                            recordRef && recordRef !== "new"
                                ? () => console.info("[Actions] Duplicate — not yet wired")
                                : undefined
                        }
                        onDelete={recordRef && recordRef !== "new" ? () => void onDeleteCurrent() : undefined}
                        // Related ▾ dropdown (Enhancement 06) — extracted from the
                        // form's <header> render plan. The toolbar OWNS this
                        // rendering; FormHeader skips `el.type === "related"` to
                        // avoid a double-render. When the form has no <related>
                        // block, this is an empty array and the trigger doesn't
                        // render at all (RelatedDropdown self-hides).
                        relatedItems={(load.data?.formPlan?.header ?? [])
                            .filter((el): el is import("../core").FormRelatedElement => el.type === "related")
                            .flatMap((el) => el.items)}
                        // Quick Meta View (Enhancement 05) — threading the loaded
                        // record + model context so the (i) trigger next to the
                        // record title can open the metadata popover. The trigger
                        // self-hides on drafts and during initial fetch.
                        record={recordQ.data}
                        modelKey={modelKey}
                        recordUuid={recordRef as string}
                        isNewRecord={recordRef === "new"}
                        // Record-strip chips (communication Enh 03) — gated
                        // `activity` component list from the form render plan.
                        activityComponents={load.data?.formPlan?.activity ?? []}
                        onTimelineJump={(filter) => setTimelineJump((p) => ({ filter, n: (p?.n ?? 0) + 1 }))}
                        onChatterMutated={() => setChatterEpoch((n) => n + 1)}
                        chatterEpoch={chatterEpoch}
                        hasWorkflow={fieldDefs.some((f) => f.workflow === true)}
                        // Workflow drawer footer next-steps (Enh 14 Stage 4) —
                        // the record's action verbs + run handlers, threaded to
                        // the drawer opened from the toolbar / cockpit link.
                        headerButtons={(load.data?.formPlan?.header ?? []).filter(
                            (el): el is FormButtonElement => el.type === "button" && !el.special,
                        )}
                        onRunButton={(b) => void onRunFormButton(b)}
                        onRunTransition={(code, name) => void onRunFormTransition(code, name)}
                        actionSlug={slug}
                    />
                    {transitioning ? (
                        <ViewLoader />
                    ) : (
                        <FormView
                            title={title}
                            recordRef={recordRef as string}
                            modelKey={modelKey}
                            actionSlug={slug}
                            fieldDefs={fieldDefs}
                            isLoading={recordQ.isLoading}
                            formPlan={load.data?.formPlan ?? null}
                            timelineJump={timelineJump}
                            timelineRefresh={chatterEpoch}
                            onChatterMutated={() => setChatterEpoch((n) => n + 1)}
                            onRunButton={(b) => void onRunFormButton(b)}
                            onRunTransition={(code, name) => void onRunFormTransition(code, name)}
                        />
                    )}
                </RecordProvider>
            ) : (
                <>
                <ActionControlPanel
                    onCreate={onCreate}
                    leftExtra={isList ? <ListToolbarSlots modelKey={modelKey} /> : undefined}
                    crumbs={crumbs}
                    title={title}
                    center={center}
                    showPager={pagerOn}
                    total={total}
                    offset={offset}
                    count={rows.length}
                    onPrev={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                    onNext={() => setOffset((o) => o + PAGE_SIZE)}
                />

            {transitioning ? (
                <ViewLoader />
            ) : isList ? (
                listPlan ? (
                    <ListView
                        columns={listPlan.columns}
                        fieldDefs={fieldDefs}
                        records={rows}
                        isLoading={records.isLoading || load.isLoading}
                        hidden={hiddenCols}
                        onToggleColumn={toggleColumn}
                        selectedIds={selectedIds}
                        onToggleRow={toggleRow}
                        onToggleAll={toggleAll}
                        order={query.order}
                        onSort={onSort}
                        groupBy={groupBy}
                        serverGroups={serverGroups}
                        onOpenRecord={onOpenRecord}
                        rowButtons={listPlan.rowButtons}
                        actionSlug={slug}
                        onRowButton={(b, r) => void onRowButton(b, r)}
                        storageKey={modelKey}
                        onWriteField={(r, n, v) => void onWriteField(r, n, v)}
                        editable={listPlan.editable}
                    />
                ) : (
                    <div className="list-view">
                        <div className="list-view__state">{load.isLoading ? "Loading…" : "This action has no list view."}</div>
                    </div>
                )
            ) : isKanban ? (
                kanbanPlan ? (
                    <KanbanView
                        plan={kanbanPlan}
                        groupAxis={activeKanbanAxis}
                        fieldDefs={fieldDefs}
                        records={rows}
                        isLoading={records.isLoading || load.isLoading}
                        groupOptions={groupOptions}
                        collapsed={foldedCols}
                        onToggleColumn={toggleFold}
                        onOpenRecord={onOpenRecord}
                        onCreate={onCreate}
                        onQuickCreate={activeKanbanAxis ? onQuickCreate : undefined}
                        onDropCard={(id, value, mode) => void onDropCard(id, value, mode)}
                    />
                ) : (
                    <div className="list-view">
                        <div className="list-view__state">{load.isLoading ? "Loading…" : "This action has no kanban view."}</div>
                    </div>
                )
            ) : (
                <div className="list-view">
                    <div className="list-view__state">The {viewMode} view lands in a later phase.</div>
                </div>
            )}
                </>
            )}
        </div>
    );
}
